import { ApolloLink, NextLink, Observable, Operation, ServerParseError } from '@apollo/client/core'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import md5 from 'md5'
import { Platform } from 'react-native'
import Config from 'react-native-config'
import Storage from '@utils/storage'
import { Login } from '@src/models/app.types'
import { ReactNativeFile } from '@src/utils/image'
import App from '../app'
import AppCheck from '../appCheck'
import AppCheckRecovery from '../appCheckRecovery'

interface OperationCache {
  eTag: string
  response: any
}

const ETAG_HEADER_KEY = 'etag'
const CACHED_OPERATIONS_KEY = 'cachedOperations'
const eTagOperations = ['clientConfig']

const buildRequestCacheKey = (operationName: string, requestDigest: string) =>
  `${CACHED_OPERATIONS_KEY}.${operationName}.${requestDigest}`

export const buildAuthLink = (abortController: AbortController, timeZone?: string) =>
  setContext(async (_operation, previousContext) => {
    // Recover from a transient keychain read failure at startup so requests aren't left
    // unauthenticated for the rest of the session (no-op once tokens have loaded). CMS requests
    // use a static key and never consume the user tokens, so skip the wait for them.
    if (previousContext.endpoint !== 'cms') {
      await Storage.ensureTokensLoaded()
    }

    const login = Storage.get<Login>('login')
    const adminLogin = Storage.get<Login>('admin_login')
    const mfaToken = Storage.get<string>(Storage.MFA_TOKEN)

    const { token } = login || {}
    const { token: adminToken } = adminLogin || {}

    // resolves from the SDK token cache; only hits Play Integrity / App Attest on expiry.
    // CMS operations never send backendHeaders, so skip the native call for them.
    const appCheckToken =
      previousContext.endpoint === 'cms'
        ? null
        : previousContext.appCheckTokenOverride || (await AppCheck.getAppCheckToken())

    return {
      ...previousContext,
      fetchOptions: {
        signal: abortController.signal,
      },
      backendHeaders: {
        authorization: token ? `Bearer ${token}` : '',
        authorization_admin: adminToken ? `Bearer ${adminToken}` : '',
        mfa_token: mfaToken || '',
        build_number: App.buildNumber,
        client_version: App.version,
        time_zone: timeZone,
        platform: Platform.OS,
        app_scheme: Config.APP_SCHEME,
        ...(appCheckToken ? { 'X-Firebase-AppCheck': appCheckToken } : {}),
      },
    }
  })

export const requestCacheLink = setContext((operation, previousContext) => {
  const operationName = operation.operationName || ''
  let requestCacheKey = undefined

  if (!eTagOperations.includes(operationName)) {
    return previousContext
  }

  const requestDigest = md5(JSON.stringify(operation))

  requestCacheKey = buildRequestCacheKey(operationName, requestDigest)

  const storedOperationCache = Storage.get<OperationCache>(requestCacheKey)
  const cacheHeaders = storedOperationCache ? { if_none_match: storedOperationCache.eTag } : {}

  return {
    ...previousContext,
    headers: {
      ...previousContext.headers,
      ...cacheHeaders,
    },
    storedOperationCache,
    requestCacheKey,
  }
})

export const afterwareLink = new ApolloLink((operation: Operation, forward: NextLink) => {
  return forward(operation).map((response) => {
    const context = operation.getContext()

    const {
      response: { headers },
    } = context

    const storedOperationCache: OperationCache | null = context.storedOperationCache
    const requestCacheKey: string | undefined = context.requestCacheKey

    if (!requestCacheKey) {
      return response
    }

    const eTag: string = headers.get(ETAG_HEADER_KEY)

    if (!storedOperationCache || eTag !== storedOperationCache.eTag) {
      Storage.set(requestCacheKey, {
        eTag,
        response,
      } as OperationCache)

      return response
    }

    return storedOperationCache.response
  })
})

export const retryLink = new RetryLink({
  attempts: {
    max: 5,
    retryIf: (error, operation) => {
      // retry if there is a network error or if we have timeout during subscription creation
      return (
        error.message === 'Network request failed' ||
        (error.statusCode === 500 && operation.operationName === 'createStripeSubscription')
      )
    },
  },
})

export const errorLink = onError(({ networkError, operation }) => {
  if (AppCheckRecovery.isAppCheckRejection(networkError)) {
    AppCheckRecovery.startRecovery()
    return
  }

  if (networkError && (networkError as ServerParseError).statusCode !== 304) {
    return
  }

  // with enabled network inspect Apollo receives empty response and it causes error.

  const storedOperationCache: OperationCache | null = operation.getContext().storedOperationCache

  if (!storedOperationCache) {
    return
  }

  const { response } = storedOperationCache

  // pass cached response down to afterwareLink
  return new Observable((observer) => {
    observer.next(response)
    observer.complete()
  })
})

export const buildMultiApiLink = (backendEndpoint: string) => {
  const backendLink = createUploadLink({
    uri: backendEndpoint,
    isExtractableFile: (value: unknown): value is ReactNativeFile =>
      value instanceof ReactNativeFile,
  })

  const cmsLink = createUploadLink({
    uri: Config.COURSES_CMS_ENDPOINT,
    isExtractableFile: (value: unknown): value is ReactNativeFile =>
      value instanceof ReactNativeFile,
  })

  return ApolloLink.split(
    (operation) => {
      const context = operation.getContext()
      return context.endpoint === 'cms'
    },
    new ApolloLink((operation, forward) => {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${Config.COURSES_CMS_API_KEY}`,
        },
      }))
      return forward(operation)
    }).concat(cmsLink),
    new ApolloLink((operation, forward) => {
      operation.setContext(({ headers = {}, backendHeaders = {} }) => ({
        headers: {
          ...headers,
          ...backendHeaders,
        },
      }))
      return forward(operation)
    }).concat(backendLink),
  )
}
